import request from '@/utils/request'

export function resolveMediaUrl(url) {
  if (!url) return ''
  const value = String(url)
  if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
    return value
  }
  const uploaded = value.match(/^\/api\/files\/([^/?#]+)$/)
  if (uploaded) {
    return '/api/file?name=' + encodeURIComponent(uploaded[1])
  }
  if (value.startsWith('/api/file?')) {
    return value
  }
  return value.split('/').map((seg, index) => (index === 0 ? seg : encodeURIComponent(seg))).join('/')
}

export function detectSpecView(product) {
  const p = product || {}
  if (p.layout) return p.layout
  const id = Number(p.id)
  if (id === 1) return 'focus'
  if (id === 2) return 'collimate'
  if (id === 3) return 'param-guide'
  if (id === 4) return 'text'
  if ([5, 6, 7].includes(id)) return 'photodiode'
  if (id === 8) return 'spectrometer'
  if ([9, 10].includes(id)) return 'xray'
  if (id >= 11 && id <= 24) return 'param'
  const specs = Array.isArray(p.specifications) ? p.specifications : []
  const first = specs[0] || {}
  if (first.type && first.rms) return 'focus'
  if (first.type && first.outputDiameter) return 'collimate'
  if (first.partNumber || first.sensitiveArea) return 'photodiode'
  if (p.kitInfo || p.kitImagePath) return 'spectrometer'
  const apps = p.applications
  if (apps && typeof apps === 'object' && !Array.isArray(apps) && Object.keys(apps).length) return 'xray'
  if (p.technicalDetails && !specs.length) return 'text'
  if (first.parameter || first.name || specs.length) return 'param'
  if (p.content && String(p.content).includes('<')) return 'html'
  return 'empty'
}

export function parseJsonField(value, fallback) {
  if (value == null || value === '') return fallback
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

export function mapProduct(product) {
  if (!product) return null
  const extra = parseJsonField(product.detailJson, {}) || {}
  const features = Array.isArray(product.features)
    ? product.features
    : parseJsonField(product.features, String(product.features || '').split(/[,，]/).map(s => s.trim()).filter(Boolean))
  const rawSpecs = Array.isArray(product.specifications)
    ? product.specifications
    : parseJsonField(product.specifications, [])
  const specifications = (rawSpecs || []).map(spec => ({
    ...spec,
    name: spec.name || spec.parameter || '',
    parameter: spec.parameter || spec.name || '',
    value: spec.value,
    imagePath: spec.imagePath ? resolveMediaUrl(spec.imagePath) : spec.imagePath
  }))
  const imageUrl = resolveMediaUrl(product.imageUrl || product.image)
  return {
    ...product,
    ...extra,
    image: imageUrl,
    imageUrl,
    shortDescription: product.shortDescription || product.description || '',
    fullDescription: product.fullDescription || product.description || product.shortDescription || '',
    description: product.description || product.shortDescription || '',
    features: features || [],
    specifications,
    applicationDescription: product.applicationDescription || extra.applicationDescription || '',
    technicalDetails: extra.technicalDetails || product.technicalDetails || '',
    kitInfo: extra.kitInfo || product.kitInfo || '',
    kitImagePath: resolveMediaUrl(extra.kitImagePath || product.kitImagePath || ''),
    dimensionImagePath: resolveMediaUrl(extra.dimensionImagePath || product.dimensionImagePath || ''),
    applications: extra.applications || product.applications || null,
    disclaimer: extra.disclaimer || product.disclaimer || '',
    layout: extra.layout || product.layout || '',
    isPublished: product.isPublished !== false
  }
}

export function mergeById(hardcoded = [], apiList = []) {
  const mapped = (apiList || []).map(item => ({ ...item }))
  const byId = new Map(mapped.map(item => [Number(item.id), item]))
  const hardcodedIds = new Set(hardcoded.map(item => Number(item.id)))
  const merged = hardcoded.map(item => {
    const apiItem = byId.get(Number(item.id))
    return apiItem ? { ...item, ...apiItem } : item
  })
  const extras = mapped.filter(item => !hardcodedIds.has(Number(item.id)))
  return [...merged, ...extras]
}

export function mergeProducts(hardcoded = [], apiList = []) {
  const apiProducts = (apiList || []).map(mapProduct).filter(Boolean)
  const byId = new Map(apiProducts.map(item => [Number(item.id), item]))
  const hardcodedIds = new Set(hardcoded.map(item => Number(item.id)))
  const merged = hardcoded.map(item => {
    const apiItem = byId.get(Number(item.id))
    if (!apiItem) return item
    return {
      ...item,
      ...apiItem,
      image: (apiItem.image && !String(apiItem.image).includes('aliyuncs.com')) ? apiItem.image : item.image,
      imageUrl: (apiItem.imageUrl && !String(apiItem.imageUrl).includes('aliyuncs.com')) ? apiItem.imageUrl : item.image,
      name: apiItem.name || item.name,
      shortDescription: apiItem.shortDescription || item.shortDescription,
      fullDescription: apiItem.fullDescription || item.fullDescription,
      description: apiItem.description || item.description,
      features: (apiItem.features && apiItem.features.length) ? apiItem.features : (item.features || []),
      specifications: (apiItem.specifications && apiItem.specifications.length) ? apiItem.specifications : item.specifications,
      applicationDescription: apiItem.applicationDescription || item.applicationDescription,
      technicalDetails: apiItem.technicalDetails || item.technicalDetails,
      kitInfo: apiItem.kitInfo || item.kitInfo,
      kitImagePath: apiItem.kitImagePath || item.kitImagePath,
      applications: apiItem.applications || item.applications,
      disclaimer: apiItem.disclaimer || item.disclaimer,
      dimensionImagePath: apiItem.dimensionImagePath || item.dimensionImagePath,
      layout: apiItem.layout || item.layout,
      content: apiItem.content || item.content
    }
  })
  const extras = apiProducts.filter(item => !hardcodedIds.has(Number(item.id)) && item.isPublished !== false)
  return [...merged, ...extras]
}

export async function fetchPublishedProducts() {
  try {
    const data = await request.get('/products/published-list', { silent: true })
    if (Array.isArray(data)) return data
    if (data && Array.isArray(data.content)) return data.content
  } catch (e) {
    // 兼容尚未升级的后端：走分页接口
  }
  try {
    const data = await request.get('/products', { params: { page: 0, size: 200, sort: 'sortOrder,asc' }, silent: true })
    const list = Array.isArray(data) ? data : (data && data.content) || []
    return list.filter(item => item.isPublished !== false)
  } catch (e) {
    return []
  }
}

export async function fetchProductById(id) {
  try {
    return await request.get(`/products/${id}`, { silent: true })
  } catch (e) {
    return null
  }
}

export async function fetchEnabledCategories() {
  try {
    const data = await request.get('/categories/enabled', { silent: true })
    return Array.isArray(data) ? data : []
  } catch (e) {
    return []
  }
}

export async function fetchPublishedBanners() {
  try {
    const data = await request.get('/banners/published', { silent: true })
    return Array.isArray(data) ? data : []
  } catch (e) {
    return []
  }
}

export async function fetchContentByCode(code) {
  try {
    const data = await request.get(`/contents/code/${code}`, { silent: true })
    if (data && data.content) return data
    return null
  } catch (e) {
    return null
  }
}
